"use client";

import { useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Check, ExternalLink, Sparkles } from "lucide-react";
import InitialSurvey from "./InitialSurvey";
import {
  CURRENT_ONBOARDING_VERSION,
  ONBOARDING_VERSION_STEP_ID,
} from "./localOnboardingSteps";
import { postSurveyFill } from "../services/survey/surveyService";
import { decodeTokenService } from "../services/decodeTokenService";
import FluxiLogo from "../assets/svg/FluxiLogo.svg";

type SurveyScreen = "welcome" | "survey" | "community" | "terms";

const WHATSAPP_COMMUNITY_LINK =
  import.meta.env.VITE_WHATSAPP_COMMUNITY_LINK ||
  "https://chat.whatsapp.com/GWvtKKvHgVFAaTaFc6aeey?mode=gi_t";

const TERMS_LINK = "https://app.fluxi.co/terms";
const FLUXI_LOGO = FluxiLogo;

const sharedBg = (
  <div
    className="fixed inset-0 z-0 pointer-events-none"
    style={{
      backgroundImage:
        "linear-gradient(180deg, rgba(62, 62, 255, 0) 0%, rgba(62, 110, 255, 0.15) 100%), linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 100%)",
    }}
  />
);

export default function StartSurvey() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    missingStepIds = [],
    isFirstTime = true,
    initialAnswers = {},
    authorizationRequired = false,
  } = (location.state || {}) as {
    missingStepIds?: string[];
    isFirstTime?: boolean;
    initialAnswers?: Record<string, unknown>;
    authorizationRequired?: boolean;
  };

  const initialScreen: SurveyScreen =
    authorizationRequired
      ? "terms"
      : isFirstTime
        ? "welcome"
        : "survey";
  const useLocalOnboardingFlow =
    isFirstTime ||
    initialAnswers[ONBOARDING_VERSION_STEP_ID] === CURRENT_ONBOARDING_VERSION;
  const [screen, setScreen] = useState<SurveyScreen>(initialScreen);
  const [accepted, setAccepted] = useState({
    terms: initialAnswers.acceptedTerms === true,
    content: initialAnswers.acceptedContentResponsibility === true,
    brands: initialAnswers.acceptedTrademarkResponsibility === true,
  });
  const [savingAuthorizations, setSavingAuthorizations] = useState(false);
  const [authorizationError, setAuthorizationError] = useState("");

  const allAccepted = accepted.terms && accepted.content && accepted.brands;
  const finishOnboarding = () => {
    if (window.innerWidth < 768) {
      navigate("/settings");
      return;
    }
    navigate("/home");
  };
  const finishWithAuthorizations = async () => {
    if (!allAccepted || savingAuthorizations) return;

    const userId = decodeTokenService.getUserIdFromToken() || localStorage.getItem("user_id") || "";
    if (!userId) {
      setAuthorizationError("No pudimos identificar tu cuenta. Intenta iniciar sesión de nuevo.");
      return;
    }

    try {
      setSavingAuthorizations(true);
      setAuthorizationError("");
      await postSurveyFill(
        {
          entity_type: "user",
          entity_id: userId,
          answers: {
            acceptedTerms: true,
            acceptedContentResponsibility: true,
            acceptedTrademarkResponsibility: true,
            acceptedTermsAt: new Date().toISOString(),
          },
        },
        "default"
      );
      finishOnboarding();
    } catch (error) {
      console.error("Error saving onboarding authorizations:", error);
      setAuthorizationError("No pudimos guardar tu autorización. Inténtalo de nuevo.");
    } finally {
      setSavingAuthorizations(false);
    }
  };

  if (screen === "survey") {
    return (
      <InitialSurvey
        missingStepIds={missingStepIds}
        initialAnswers={initialAnswers}
        showAccountPreferencesInFlow
        useLocalOnboardingFlow={useLocalOnboardingFlow}
        onComplete={() => setScreen("community")}
      />
    );
  }

  if (screen === "community") {
    return (
      <OnboardingShell>
        <div className="w-full max-w-[540px] bg-white/86 border border-white rounded-[30px] shadow-[0_22px_70px_rgba(49,71,176,0.14)] px-6 py-8 sm:px-9 sm:py-10 text-center">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EEF1FF] text-[#2F36D9]">
            <Sparkles className="h-6 w-6" />
          </div>
          <h1 className="text-[28px] sm:text-[36px] font-extrabold leading-tight text-[#111827]">
            No te pierdas nuestros eventos exclusivos
          </h1>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#3D3D3D]">
            Únete a la comunidad para recibir actualizaciones y contenido de valor.
          </p>
          <div className="mt-7 flex flex-col gap-3">
            <a
              href={WHATSAPP_COMMUNITY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#25D366] px-5 py-4 text-base sm:text-lg font-bold text-white shadow-[0_14px_28px_rgba(37,211,102,0.24)] transition hover:scale-[1.02] hover:bg-[#1ebe5d] active:scale-95"
            >
              Unirme a la comunidad
              <ExternalLink className="h-5 w-5" />
            </a>
            <button
              type="button"
              onClick={() => setScreen("terms")}
              className="w-full rounded-2xl border border-[#D9DDE8] bg-white px-5 py-4 text-base sm:text-lg font-bold text-[#2F36D9] transition hover:border-[#2F36D9]/50 hover:bg-[#F7F8FF] active:scale-95"
            >
              Ya estoy en la comunidad
              <ArrowRight className="ml-2 inline h-5 w-5 align-[-4px]" />
            </button>
          </div>
        </div>
      </OnboardingShell>
    );
  }

  if (screen === "terms") {
    return (
      <OnboardingShell>
        <div className="w-full max-w-[590px] bg-white/88 border border-white rounded-[30px] shadow-[0_22px_70px_rgba(49,71,176,0.14)] px-6 py-8 sm:px-9 sm:py-10">
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#2F36D9]">
            Una última cosa
          </p>
          <h1 className="mt-3 text-[28px] sm:text-[36px] font-extrabold leading-tight text-[#111827]">
            Detalles finales para abrirte la puerta a Fluxi
          </h1>
          <div className="mt-7 flex flex-col gap-3">
            <TermsCheck
              checked={accepted.terms}
              onChange={() => setAccepted((prev) => ({ ...prev, terms: !prev.terms }))}
            >
              Leí y acepto los{" "}
              <a
                href={TERMS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[#2F36D9] underline-offset-4 hover:underline"
              >
                términos y condiciones
              </a>{" "}
              de Fluxi.
            </TermsCheck>
            <TermsCheck
              checked={accepted.content}
              onChange={() => setAccepted((prev) => ({ ...prev, content: !prev.content }))}
            >
              Entiendo que soy responsable de revisar y ajustar el contenido creado por Fluxi,
              reemplazando testimonios y promesas para que vayan acordes con la realidad.
            </TermsCheck>
            <TermsCheck
              checked={accepted.brands}
              onChange={() => setAccepted((prev) => ({ ...prev, brands: !prev.brands }))}
            >
              Confirmo que no usaré imágenes, nombres, logos ni elementos de marcas registradas
              sin contar con la autorización correspondiente.
            </TermsCheck>
          </div>
          <button
            type="button"
            disabled={!allAccepted || savingAuthorizations}
            onClick={finishWithAuthorizations}
            className="fluxi-pop mt-6 inline-flex min-h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-[#2F36D9] px-5 py-3 text-sm sm:text-base font-extrabold uppercase text-white shadow-[0_14px_28px_rgba(47,54,217,0.22)] transition hover:bg-[#252AB8] disabled:opacity-40 disabled:shadow-none"
          >
            {savingAuthorizations ? "Guardando..." : "ACEPTO"}
            <ArrowRight className="h-5 w-5" />
          </button>
          {authorizationError && (
            <p className="mt-3 text-center text-sm font-semibold text-red-600">
              {authorizationError}
            </p>
          )}
        </div>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell>
      <div className="flex w-full max-w-[520px] flex-col items-center text-center">
        <p className="animate-[fluxiWelcomeIn_0.68s_ease-out_both] text-[28px] sm:text-[34px] font-extrabold text-[#111827]">
          Bienvenido a
        </p>
        <img
          src={FLUXI_LOGO}
          alt="Fluxi"
          className="mt-4 w-[220px] opacity-100 mix-blend-normal sm:w-[300px] animate-[fluxiLogoIn_1.05s_0.48s_ease-out_both]"
        />
        <button
          type="button"
          onClick={() => setScreen("survey")}
          className="fluxi-shine relative mt-10 inline-flex w-full max-w-[330px] items-center justify-center gap-2 overflow-hidden rounded-2xl bg-[#2F36D9] px-6 py-4 text-base sm:text-lg font-extrabold text-white shadow-[0_18px_42px_rgba(47,54,217,0.28)] transition hover:scale-[1.02] hover:bg-[#252AB8] active:scale-95 animate-[fluxiButtonIn_0.65s_1.42s_ease-out_both]"
        >
          <span>Personaliza tu cuenta</span>
        </button>
      </div>
    </OnboardingShell>
  );
}

function OnboardingShell({ children }: { children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto bg-[#F8F9FC] font-['Plus_Jakarta_Sans',sans-serif]">
      <style>{`
        @keyframes fluxiWelcomeIn {
          0% { opacity: 0; transform: translateY(8px); filter: blur(3px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }

        @keyframes fluxiLogoIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.96); filter: blur(4px); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }

        @keyframes fluxiButtonIn {
          0% { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes fluxiShine {
          0% { transform: translateX(-130%) skewX(-18deg); opacity: 0; }
          12% { opacity: 0.75; }
          38% { opacity: 0.45; }
          54%, 100% { transform: translateX(170%) skewX(-18deg); opacity: 0; }
        }

        @keyframes fluxiPop {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.035); }
        }

        .fluxi-shine::after {
          content: "";
          position: absolute;
          inset: -35% auto -35% 0;
          width: 42%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.78), transparent);
          animation: fluxiShine 1.55s ease-in-out infinite;
          animation-delay: 0s;
          pointer-events: none;
        }

        .fluxi-pop {
          animation: fluxiPop 1.35s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .fluxi-shine::after,
          .fluxi-pop {
            animation: none;
          }
        }
      `}</style>
      {sharedBg}
      <main className="relative z-10 flex min-h-screen items-center justify-center px-4 py-8 sm:px-6">
        {children}
      </main>
    </div>
  );
}

function TermsCheck({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer gap-3 rounded-2xl border border-[#D9DDE8] bg-white px-4 py-4 text-left transition hover:border-[#2F36D9]/45 hover:bg-[#F7F8FF]">
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
      <span
        className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border transition ${
          checked ? "border-[#2F36D9] bg-[#2F36D9]" : "border-[#B8BECC] bg-white"
        }`}
      >
        {checked && <Check className="h-4 w-4 text-white" />}
      </span>
      <span className="text-sm sm:text-base leading-relaxed text-[#3D3D3D]">{children}</span>
    </label>
  );
}
